import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';

/**
 * 기관 담당자 추가/편집 공통 팝업
 * config.mode: 'add' | 'edit'
 * config.initialData: 편집 시 기존 데이터
 * onClose(submit, { formData })
 */
const OrgManagerFormPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const mode = config?.mode || 'add';
  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState({
    orgName: '',
    name: '',
    role: '',
    contact: '',
    status: '활성'
  });
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (config?.initialData) {
      setFormData({
        orgName: config.initialData.orgName ?? '',
        name: config.initialData.name ?? '',
        role: config.initialData.role ?? '',
        contact: config.initialData.contact ?? '',
        status: config.initialData.status ?? '활성'
      });
    }
  }, [config?.initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }
    if (!formData.name?.trim()) {
      setValidated(true);
      return;
    }
    const payload = { ...formData };
    if (config?.initialData?.id != null) payload.id = config.initialData.id;
    onClose(true, { formData: payload });
  };

  const handleCancel = () => onClose(false);
  const handleConfirm = () => {
    const form = document.querySelector('#org-manager-form-popup form');
    if (form) form.requestSubmit();
  };

  return (
    <PopupWrapper
      config={{
        ...config,
        title: isEdit ? t('popup.orgManagerForm.titleEdit') : t('popup.orgManagerForm.titleAdd'),
        width: config?.width ?? 480,
        leftButton: { text: t('popup.common.cancel'), color: 'btn-secondary', submit: false },
        rightButton: { text: t('popup.common.confirm'), color: 'btn-primary', submit: true }
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-org-manager-form"
      id="org-manager-form-popup"
    >
      <div className="main-popup-body">
        <div className="main-popup-form">
          <form
            className={`needs-validation ${validated ? 'was-validated' : ''}`}
            noValidate
            onSubmit={handleSubmit}
          >
            <div className="row g-3">
              <div className="col-12">
                <label htmlFor="org-manager-orgName" className="form-label">
                  {t('popup.orgManagerForm.orgLabel')} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="org-manager-orgName"
                  name="orgName"
                  value={formData.orgName}
                  onChange={handleChange}
                  required
                />
                <div className="invalid-feedback">{t('popup.orgManagerForm.orgRequired')}</div>
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="org-manager-name" className="form-label">
                  {t('popup.common.name')} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="org-manager-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <div className="invalid-feedback">{t('popup.orgManagerForm.nameRequired')}</div>
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="org-manager-role" className="form-label">{t('popup.orgManagerForm.roleLabel')}</label>
                <input
                  type="text"
                  className="form-control"
                  id="org-manager-role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="org-manager-contact" className="form-label">{t('popup.orgManagerForm.contactLabel')}</label>
                <input
                  type="text"
                  className="form-control"
                  id="org-manager-contact"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="org-manager-status" className="form-label">{t('popup.orgManagerForm.statusLabel')}</label>
                <select
                  className="form-select"
                  id="org-manager-status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="활성">{t('popup.orgManagerForm.statusActive')}</option>
                  <option value="대기">{t('popup.orgManagerForm.statusPending')}</option>
                  <option value="비활성">{t('popup.orgManagerForm.statusInactive')}</option>
                </select>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        <button
          type="button"
          className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`}
          onClick={handleCancel}
        >
          {t('popup.common.cancel')}
        </button>
        <button
          type="button"
          className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`}
          onClick={handleConfirm}
        >
          {t('popup.common.confirm')}
        </button>
      </div>
    </PopupWrapper>
  );
};

export default OrgManagerFormPopup;
